import React, { useMemo, useState, useRef, useEffect } from 'react';
import useGameBase from '../../hooks/useGameBase';
import { useSound } from '../../utils/sound/SoundProvider';
import SpectatorPerspectiveSelector from "./SpectatorPerspectiveSelector";

function Cell({ r, c, letter, selected, className, size = 32 }) {
  const fs = Math.max(12, Math.round(size * 0.5));
  return (
    <div
      style={{ width: `${size}px`, height: `${size}px`, fontSize: `${fs}px` }}
      className={`flex items-center justify-center border select-none ${selected ? 'bg-blue-300 dark:bg-blue-400' : 'bg-white dark:bg-gray-800'} ${className}`}
      role="gridcell"
      aria-rowindex={r}
      aria-colindex={c}
    >{letter}</div>
  );
}

export default function WordSearchGame({ roomId, gameState = {}, gamePlayers = [], user = {}, socket, isSpectator = false }) {
  // --- Spectator Perspective logic (modular, opt-in, matches Canasta) ---
  const perspectiveOptions = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const [spectatorViewPlayerId, setSpectatorViewPlayerId] = useState(null);
  const perspectivePlayer = useMemo(() => {
    if (!isSpectator || !spectatorViewPlayerId) return null;
    return perspectiveOptions.find((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    }) || null;
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  useEffect(() => {
    if (!isSpectator) {
      setSpectatorViewPlayerId(null);
      return;
    }
    if (!Array.isArray(perspectiveOptions) || perspectiveOptions.length === 0) {
      setSpectatorViewPlayerId(null);
      return;
    }
    const exists = perspectiveOptions.some((player) => {
      const playerId = player?.userId || player?.socketId || player?.id || player?.username;
      return String(playerId) === String(spectatorViewPlayerId);
    });
    if (!exists) {
      const first = perspectiveOptions[0];
      setSpectatorViewPlayerId(first?.userId || first?.socketId || first?.id || first?.username || null);
    }
  }, [perspectiveOptions, isSpectator, spectatorViewPlayerId]);

  const { submitAction, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId, perspectivePlayer });

  const matchesPerspectivePlayer = useMemo(() => {
    const idSet = new Set((perspectiveIds || []).filter(Boolean).map((value) => String(value)));
    return (player) => {
      if (!player || idSet.size === 0) return false;
      return [player.socketId, player.userId, player.id, player.username]
        .filter((value) => value !== undefined && value !== null)
        .some((value) => idSet.has(String(value)));
    };
  }, [perspectiveIds]);

  const players = useMemo(() => {
    if (Array.isArray(gamePlayers) && gamePlayers.length > 0) return gamePlayers;
    if (Array.isArray(gameState?.players) && gameState.players.length > 0) return gameState.players;
    return [];
  }, [gamePlayers, gameState?.players]);

  const viewedPlayer = useMemo(() => {
    return players.find(matchesPerspectivePlayer) || null;
  }, [players, matchesPerspectivePlayer]);

  const viewedPlayerSocketId = useMemo(() => {
    if (viewedPlayer?.socketId) return viewedPlayer.socketId;
    if (socket?.id && !isSpectator) return socket.id;
    return perspectiveIds.find(Boolean) || null;
  }, [viewedPlayer?.socketId, socket?.id, isSpectator, perspectiveIds]);

  const grid = gameState.grid || [];
  const gridSize = gameState.gridSize || (grid.length || 0);
  const wordsPlaced = (gameState.words || []);
  const foundBy = useMemo(() => (gameState && gameState.foundBy) ? gameState.foundBy : {}, [gameState]);
  const myFound = useMemo(() => Array.isArray(foundBy[viewedPlayerSocketId]) ? foundBy[viewedPlayerSocketId] : [], [foundBy, viewedPlayerSocketId]);
  const [selectPath, setSelectPath] = useState([]); // array of [r,c]
  // detect user's dark-mode preference: explicit user setting first, fall back to OS preference
  const prefersDark = (user && (typeof user.darkMode !== 'undefined' ? user.darkMode : (typeof user.prefersDark !== 'undefined' ? user.prefersDark : null))) ?? (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  // smaller visual cells to make the grid narrower while keeping it square
  const cellSize = 32;
  const pad = 8; // padding around grid inside container so overlays don't get clipped
  const sound = useSound();
  const containerRef = useRef(null);
  const selectingRef = useRef(false);
  const startRef = useRef(null);

  const dirs = [ [0,1], [1,0], [1,1], [1,-1], [0,-1], [-1,0], [-1,-1], [-1,1] ];

  

  const getCellFromPointer = (clientX, clientY) => {
    const rect = containerRef.current && containerRef.current.getBoundingClientRect();
    if (!rect) return null;
    const x = clientX - rect.left - pad;
    const y = clientY - rect.top - pad;
    const c = Math.floor(x / cellSize);
    const r = Math.floor(y / cellSize);
    if (r < 0 || c < 0 || r >= gridSize || c >= gridSize) return null;
    return [r,c];
  };

  const chooseDir = (dr, dc) => {
    // pick nearest of 8 directions by angle
    const angle = Math.atan2(dr, dc);
    let best = null; let bestDiff = Infinity;
    for (const d of dirs) {
      const a = Math.atan2(d[0], d[1]);
      let diff = Math.abs(a - angle);
      if (diff > Math.PI) diff = Math.abs(diff - 2*Math.PI);
      if (diff < bestDiff) { bestDiff = diff; best = d; }
    }
    return best;
  };

  const buildPath = (start, dir, steps) => {
    const arr = [];
    for (let k = 0; k <= steps; k++) {
      const r = start[0] + dir[0]*k;
      const c = start[1] + dir[1]*k;
      if (r < 0 || c < 0 || r >= gridSize || c >= gridSize) break;
      arr.push([r,c]);
    }
    return arr;
  };

  // const foundSet = useMemo(() => new Set(myFound || []), [myFound]);

  // Pointer-based drag selection handlers (support mouse/touch via pointer events)
  const onPointerDown = (e) => {
    if (isSpectator) return;
    if (!containerRef.current) return;
    if (!gameState || !gameState.started) return; // only allow during active game
    // only accept primary button
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    const cell = getCellFromPointer(e.clientX, e.clientY);
    if (!cell) return;
    selectingRef.current = true;
    startRef.current = cell;
    setSelectPath([cell]);
    try { e.currentTarget.setPointerCapture && e.currentTarget.setPointerCapture(e.pointerId); } catch (ex) {}
  };

  const onPointerMove = (e) => {
    if (isSpectator) return;
    if (!selectingRef.current) return;
    e.preventDefault();
    const cell = getCellFromPointer(e.clientX, e.clientY);
    if (!cell) {
      // if pointer outside, clamp to edges
      return;
    }
    const start = startRef.current;
    if (!start) return;
    // if same as start and path length 1, do nothing
    if (cell[0] === start[0] && cell[1] === start[1]) {
      setSelectPath([start]);
      return;
    }
    const dr = cell[0] - start[0];
    const dc = cell[1] - start[1];
    const dir = chooseDir(dr, dc);
    // compute steps as projection of vector onto dir
    const denom = dir[0]*dir[0] + dir[1]*dir[1];
    const kRaw = (dr*dir[0] + dc*dir[1]) / (denom || 1);
    const steps = Math.max(0, Math.round(kRaw));
    const path = buildPath(start, dir, steps);
    setSelectPath(path);
  };

  const finishSelection = (submit = true) => {
    if (isSpectator) return;
    selectingRef.current = false;
    const path = selectPath;
    if (path && path.length >= 2 && submit) {
      const word = path.map(([rr,cc]) => (grid[rr] && grid[rr][cc]) || '').join('');
      submitAction({ type: 'submitWord', word }).then((ack) => {
        try {
          // Only play positive sound for confirmed server success; ignore optimistic timeouts
          const confirmedSuccess = ack && (ack.ok || ack.status === 'ok') && !ack.optimistic;
          if (confirmedSuccess) {
            try { sound && sound.play && sound.play('ui:positive'); } catch (e) {}
          }
        } catch (e) {}
      }).catch(()=>{});
    }
    setSelectPath([]);
    startRef.current = null;
  };

  const onPointerUp = (e) => {
    if (isSpectator) return;
    if (!selectingRef.current) return;
    e.preventDefault();
    try { e.currentTarget.releasePointerCapture && e.currentTarget.releasePointerCapture(e.pointerId); } catch (ex) {}
    finishSelection(true);
  };

  // (analytics centralized in useGameBase)

  // render word list; in find_most mode words found by anyone are crossed off globally, in find_all each player sees their own found words crossed
  const mode = gameState.mode || (gameState.config && gameState.config.mode) || 'find_most';

  const showSpectatorPerspectiveSelector = isSpectator && perspectiveOptions.length > 0;

  return (
    <div className="wordsearch-game p-4 text-gray-900 dark:text-gray-100">
      {showSpectatorPerspectiveSelector && (
        <SpectatorPerspectiveSelector
          players={perspectiveOptions}
          value={spectatorViewPlayerId || ""}
          onChange={setSpectatorViewPlayerId}
          className="max-w-sm mb-4"
        />
      )}
      <div>
        <div className="mb-3">
          <div ref={containerRef} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={() => { if (selectingRef.current) finishSelection(false); }} style={{ position: 'relative', width: gridSize * cellSize + pad * 2, height: gridSize * cellSize + pad * 2, touchAction: 'none' }}>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${gridSize}, ${cellSize}px)`, position: 'absolute', left: pad, top: pad }}>
              {grid.map((row, r) => row.map((letter, c) => (
                <Cell key={`${r}:${c}`} r={r} c={c} letter={letter} selected={selectPath.some(p=>p[0]===r && p[1]===c)} size={cellSize} />
              )))}
            </div>

            {/* SVG overlay for per-player found-word loops (pointer-events none so grid remains clickable) */}
            <svg width={gridSize * cellSize + pad * 2} height={gridSize * cellSize + pad * 2} style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}>
              {/** Determine placements and who found them **/}
              {Array.isArray(gameState.placements) ? gameState.placements.map((pl, idx) => {
                // find which player(s) found this word
                const viewerFound = Array.isArray(foundBy[viewedPlayerSocketId]) && foundBy[viewedPlayerSocketId].includes(pl.word);
                const gameFinished = !!gameState.finished;
                if (!viewerFound && !gameFinished) return null;
                // draw for each finder a rounded loop along the first and last coord
                const coords = Array.isArray(pl.coords) ? pl.coords : [];
                if (coords.length === 0) return null;
                const first = coords[0]; const last = coords[coords.length - 1];
                const fx = first[1] * cellSize + cellSize/2 + pad; const fy = first[0] * cellSize + cellSize/2 + pad;
                const lx = last[1] * cellSize + cellSize/2 + pad; const ly = last[0] * cellSize + cellSize/2 + pad;
                // compute a single narrow rounded rectangle centered between first and last letter
                const dx = lx - fx; const dy = ly - fy;
                const dist = Math.sqrt(dx*dx + dy*dy) || 1;
                const cx = (fx + lx) / 2; const cy = (fy + ly) / 2;
                const angle = Math.atan2(dy, dx) * 180 / Math.PI;
                // narrow loop: width spans the word length plus small padding, height is narrower than a cell
                const padAlong = cellSize * 0.7; // extra along-word padding
                const loopWidth = Math.max(8, dist + padAlong);
                const loopHeight = Math.max(6, cellSize * 0.6); // narrower than cell
                const rx = Math.max(4, Math.min(12, Math.round(loopHeight / 2)));
                // no perpendicular offset needed for single viewer
                const centerX = cx;
                const centerY = cy;
                // rect x/y is top-left before rotation
                const x = centerX - loopWidth / 2;
                const y = centerY - loopHeight / 2;
                const transform = `rotate(${angle}, ${centerX}, ${centerY})`;
                if (viewerFound) {
                  const color = prefersDark ? '#34d399' : '#0f766e'; // lighter teal in dark mode
                  return (
                    <rect key={`${idx}:found`} x={x} y={y} width={loopWidth} height={loopHeight} rx={rx} ry={rx}
                      fill="none" stroke={color} strokeWidth={3} strokeOpacity={0.95} transform={transform} />
                  );
                }
                // viewer did not find this word; if game finished, show red loop
                if (!viewerFound && gameFinished) {
                  const color = prefersDark ? '#fb7185' : '#dc2626'; // softer/pinkish red in dark mode
                  return (
                    <rect key={`${idx}:missed`} x={x} y={y} width={loopWidth} height={loopHeight} rx={rx} ry={rx}
                      fill="none" stroke={color} strokeWidth={3} strokeOpacity={0.95} transform={transform} />
                  );
                }
                return null;
              }) : null}
            </svg>
          </div>
        </div>

        <div>
          <h4 className="font-semibold">Words ({wordsPlaced.length})</h4>
          <div className="mt-2 flex flex-wrap gap-2">
            {wordsPlaced.map(w => {
              const foundGlobal = Object.keys(foundBy || {}).some(k => (foundBy[k] || []).includes(w));
              const foundLocal = (myFound || []).includes(w);
              const showStruck = (mode === 'find_most') ? foundGlobal : foundLocal;
              return (
                <div key={w} className={`px-2 py-1 rounded border ${showStruck ? 'line-through text-gray-400 border-gray-200 dark:border-gray-700' : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700'}`}>
                  {w}
                </div>
              );
            })}
          </div>

          <div className="mt-4 text-sm">
            <div className="font-semibold">Players</div>
            <div className="mt-2 space-y-1">
              {players.map(p => (
                <div key={p.socketId} className="flex justify-between">
                  <div>{p.username}</div>
                  <div className="text-sm text-gray-600">{(gameState.perPlayerFoundCount && gameState.perPlayerFoundCount[p.socketId]) || 0}/{wordsPlaced.length}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// guide
WordSearchGame.Guide = function WordSearchGuide() {
  return (
    <div>
      <h3 className="font-bold">Word Search</h3>
      <p>Find words by dragging over the letters. Let go to submit the word.</p>
    </div>
  );
};
